import { moment } from '@hotjar/webapp/lib/moment';

/**
 * Formats epoch timestamps as the specified format. Format defaults to: `Do MMMM YYYY`
 */
export function epochDate(epochTime?: number, format = 'Do MMMM YYYY'): string {
  if (typeof epochTime === 'undefined') {
    return '';
  }
  return moment.unix(epochTime).format(format);
}

/**
 * Formats epoch timestamps as a ISO 8601 datetime (YYYY-MM-DD HH:MM:SS)
 */
export function epochDateTime(epochTime?: number): string {
  if (typeof epochTime === 'undefined') {
    return ':';
  }
  return moment(epochTime * 1000).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Format epoch timestamps using "time ago format" or using "date format" if the date is older than
 * verboseThreshold (defaults to a week).
 *
 * Example: "2 days ago" (time ago format)
 *          "7th January 2019" (date format)
 *
 * @param epochTime Epoch time (seconds)
 * @param verboseThreshold Number of seconds before switching to date format.
 * @param excludeYear Set to true to exclude the year from the date format. false by default
 * @param excludeTime Set to true to exclude time in the date format (e.g. 21 Apr, 18:39) true by default
 */
// FIXME: Please fix this. You may refer to CFE-266 for more details.
// eslint-disable-next-line func-params-args/func-params
export function timeAgo(
  epochTime: number,
  verboseThreshold?: number | null,
  excludeYear = false,
  excludeTime = true,
): string {
  const epochTimeMs = epochTime * 1000; // Convert the seconds to milliseconds

  const momentizedDate = moment(epochTimeMs);
  const verboseMoment = moment().subtract(verboseThreshold ?? 604800, 's');

  // If earlier than verboseThreshold we return time ago format
  if (!momentizedDate.isBefore(verboseMoment)) {
    return momentizedDate.fromNow();
  }

  // If date is before the start of this year, we add the year (unless asked not to).
  const showYear = !excludeYear && momentizedDate.isBefore(moment().startOf('year'));

  let format = '';
  if (excludeTime && showYear) {
    format = 'Do MMMM YYYY';
  } else if (excludeTime && !showYear) {
    format = 'Do MMMM';
  } else if (!excludeTime && showYear) {
    format = 'D MMM YYYY, HH:mm';
  } else {
    format = 'D MMM, HH:mm';
  }

  return momentizedDate.format(format);
}

/**
 * Get a month's name from its index.
 * e.g. 2 => 'March'
 * e.g. 11 => 'December'
 *
 * @param index The month's index (0 - 11).
 * @param short Set to true to return "Jan" instead of "January".
 * @returns String
 */
export function readableMonth(index: number, short = false): string {
  const formatString = short ? 'MMM' : 'MMMM';

  if (index < 0 || index > 11) {
    return '';
  }

  return moment(index + 1, 'M').format(formatString);
}

/**
 * Return how much time difference in days there is between a date and now().
 *
 * @param epochTime The epoch time to base calculation on.
 * @param limitToZero Limit the lowest returnable value to 0.
 */
export function daysFromNow(epochTime: number, limitToZero?: boolean): number {
  const difference = moment(epochTime * 1000).diff(moment(), 'days', true);

  return limitToZero && difference < 0 ? 0 : difference;
}
